import { useEffect, useId, useMemo, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import { cn } from '@/utils/cn';
import type { SelectOption } from '@/components/ui/Select';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import { useDebounce } from '@/hooks/useDebounce';

export interface SearchableSelectProps {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  loading?: boolean;
  /** True while a server search request is in flight. */
  searching?: boolean;
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** Filter options in the dropdown locally (default true). Set false when options come from the server. */
  filterLocally?: boolean;
  /** Called with debounced search text — use for backend search. */
  onSearchQuery?: (query: string) => void;
  searchDebounceMs?: number;
}

export function SearchableSelect({
  label,
  error,
  hint,
  options,
  placeholder = 'Select…',
  value = '',
  onChange,
  disabled,
  loading,
  searching = false,
  className,
  id,
  name,
  required,
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches',
  filterLocally = true,
  onSearchQuery,
  searchDebounceMs = 300,
}: SearchableSelectProps) {
  const autoId = useId();
  const fieldId = id ?? name ?? autoId;
  const { required: isRequired } = parseFieldLabel(label, required);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, searchDebounceMs);
  const serverSearch = Boolean(onSearchQuery);

  const selected = options.find((o) => o.value === value);
  const displayLabel = selected?.label ?? (loading ? 'Loading…' : placeholder);

  useEffect(() => {
    if (!serverSearch) return;
    onSearchQuery?.(debouncedQuery);
  }, [debouncedQuery, onSearchQuery, serverSearch]);

  const filtered = useMemo(() => {
    if (!filterLocally || serverSearch) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q),
    );
  }, [options, query, filterLocally, serverSearch]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const pick = (next: string) => {
    onChange?.(next);
    setOpen(false);
  };

  return (
    <div className={cn('relative min-w-0 w-full', className)} ref={rootRef}>
      <FieldLabel htmlFor={fieldId} label={label} required={required} />
      {hint && !error && <p className="mb-1.5 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
      <button
        type="button"
        id={fieldId}
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && !loading && setOpen((o) => !o)}
        className={cn(
          'input flex w-full min-w-0 items-center justify-between gap-2 text-left',
          !selected && 'text-slate-500 dark:text-slate-400',
          selected && 'text-slate-900 dark:text-white',
          error && 'input-error',
          (disabled || loading) && 'cursor-not-allowed opacity-60',
        )}
      >
        <span className="truncate">{displayLabel}</span>
        <KeyboardArrowDownIcon
          className={cn('shrink-0 text-slate-500 transition-transform dark:text-slate-400', open && 'rotate-180')}
          fontSize="small"
        />
      </button>
      {name && <input type="hidden" name={name} value={value} required={isRequired} readOnly />}
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full min-w-[14rem] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800">
          <div className="border-b border-slate-100 p-2 dark:border-slate-700">
            <div className="relative">
              <SearchIcon
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                sx={{ fontSize: 18 }}
              />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="input w-full py-1.5 pl-8 pr-2 text-sm"
                aria-label={searchPlaceholder}
              />
            </div>
          </div>
          <ul role="listbox" className="max-h-56 overflow-y-auto py-1">
            {placeholder && (
              <li role="option">
                <button
                  type="button"
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700',
                    value === '' && 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                  )}
                  onClick={() => pick('')}
                >
                  {placeholder}
                </button>
              </li>
            )}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                {searching ? 'Searching…' : emptyMessage}
              </li>
            )}
            {filtered.map((opt) => (
              <li key={opt.value} role="option" aria-selected={value === opt.value}>
                <button
                  type="button"
                  disabled={opt.disabled}
                  className={cn(
                    'w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-100 dark:hover:bg-slate-700',
                    value === opt.value &&
                      'bg-primary-50 font-medium text-primary-700 dark:bg-primary-950/50 dark:text-primary-300',
                  )}
                  onClick={() => !opt.disabled && pick(opt.value)}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
